'use client';

import type Keycloak from 'keycloak-js';
import { createContext, FC, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { getAdminToken, initKeycloakAdmin, keycloakAdmin } from '@/configs/keycloak-admin.config';

type StudioKeycloakContext = {
  keycloak: Keycloak | undefined;
};

const StudioKeycloakCtx = createContext<StudioKeycloakContext>({
  keycloak: undefined,
});

export const useStudioKeycloak = () => useContext(StudioKeycloakCtx);

export const StudioKeycloakProvider: FC<PropsWithChildren> = ({ children }) => {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    initKeycloakAdmin()
      .then(authenticated => {
        if (!authenticated) {
          keycloakAdmin?.login();
          return;
        }
        setInitialized(true);
      })
      .catch(() => {
        keycloakAdmin?.login();
      });
  }, []);

  useEffect(() => {
    if (!initialized || !keycloakAdmin) return;
    const interval = setInterval(() => {
      getAdminToken().catch(() => {});
    }, 30_000);
    return () => clearInterval(interval);
  }, [initialized]);

  if (!initialized) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-default-500">Loading Studio…</p>
      </div>
    );
  }

  const roles = keycloakAdmin?.tokenParsed?.realm_access?.roles ?? [];
  const hasAccess = roles.includes('admin');

  if (!hasAccess) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-red-500">Access denied.</p>
      </div>
    );
  }

  return (
    <StudioKeycloakCtx.Provider value={{ keycloak: keycloakAdmin }}>
      {children}
    </StudioKeycloakCtx.Provider>
  );
};
